import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import styled from 'styled-components';
import { Button, Grid, Header, Icon, Message, Popup, Progress, Segment } from 'semantic-ui-react';
import Accordion from 'semantic-ui-react/dist/commonjs/modules/Accordion';
import {
  addTempTreatmentPrescription,
  addTreatmentPrescription,
  deleteTempTreatmentPrescription,
  deleteTreatmentPrescription,
  getTreatmentDownloadKey
} from 'services/treatmentPrescriptionServices';
import axios, { CancelTokenSource } from 'axios';
import PxGrid from 'components/layout/PxGrid';
import { TreatmentPrescriptionDto } from 'ts-types/api.types';
import DeleteActionButton from 'components/DeleteActionButton';
import DropZone from 'components/dropzone/DropZone';
import { FileRejection } from 'react-dropzone';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import TreatmentPrescriptionPreviewModal from 'components/treatments/segments/TreatmentPrescriptionPreviewModal';
import { EditTreatmentValues } from 'components/treatments/TreatmentsContext';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

const StyledButtonContainer = styled.span`
    float: right;
`;

const StyledButton = styled(Button)`
  padding: 2px !important;
  margin-bottom: 2px !important;
  background: transparent !important;
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const NoOverflowDiv = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledBorderGrid = styled(Grid)`
     border-bottom: 1px solid rgba(34,36,38,.1);
`;

const TreatmentPrescriptionBox = styled(Segment)`
     width: 100%;
     height: 4.5rem;
     padding: 0.75rem;
`;

const FileType = styled.div`
    color: grey;
`;

interface Props extends MessageSourceProps {
  treatment: EditTreatmentValues,
  setUploading: (uploading: boolean) => void,
  setTempTreatmentId: (tempTreatmentId?: number) => void,
  readOnly: boolean
}

interface State {
  active: boolean,
  treatmentId?: number,
  treatmentPrescription?: TreatmentPrescriptionDto,
  errorCodes?: Array<string>,
  uploading: boolean,
  uploadingProgress?: number,
  popupOpen: boolean,
  file?: File,
  previewOpen: boolean,
  treatmentDownloadKey?: string,
  tempTreatmentId?: number,
  cancelTokenSource: CancelTokenSource
}

class TreatmentPrescriptionSegment extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const treatment = props.treatment;
    const treatmentPrescription = treatment ? treatment.treatmentPrescription : undefined;
    const treatmentId = treatment ? treatment.id : undefined;

    this.state = {
      active: false,
      uploading: false,
      popupOpen: false,
      previewOpen: false,
      treatmentId,
      treatmentPrescription,
      cancelTokenSource
    }
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {
      active,
      previewOpen,
      treatmentPrescription,
      treatmentDownloadKey
    } = this.state;

    return (
        <StyledBorderGrid>
          <Accordion fluid>
            <Accordion.Title active={active} onClick={() => this.toggleAccordion(!active)}>
              <h4><Icon name='dropdown'/>{getMessage('treatmentPrescription')}</h4>
            </Accordion.Title>
            <Accordion.Content active={active}>
              {this.renderTreatmentPrescriptionSegment()}
              {
                previewOpen &&
                <TreatmentPrescriptionPreviewModal
                    treatmentDownloadKey={treatmentDownloadKey}
                    treatmentPrescription={treatmentPrescription}
                    modalOpen={previewOpen}
                    onCloseModal={() => this.setPreviewOpen(false)}
                />
              }
            </Accordion.Content>
          </Accordion>
        </StyledBorderGrid>
    );
  };

  renderTreatmentPrescriptionSegment = () => {
    const {treatmentPrescription, errorCodes, popupOpen} = this.state;
    const {getMessage, readOnly} = this.props;

    const errorItems = errorCodes ?
        errorCodes.map(ec => {
          return (
              <Message.Item key={ec}>{getMessage(`error.${ec}`)}</Message.Item>
          );
        }) : [];

    return (
        <PxGrid>
          {errorCodes && (
              <Grid.Row>
                <Grid.Column>
                  <StyledErrorMessage onDismiss={() => this.setErrorCodes()}>
                    <Message.Header>{getMessage("error.header")}</Message.Header>
                    {errorItems}
                  </StyledErrorMessage>
                </Grid.Column>
              </Grid.Row>
          )}
          <Grid.Row>
            {treatmentPrescription ? this.renderTreatmentPrescriptionInformation() : ""}
            {!readOnly && (
                <Grid.Column width={6}>
                  <Popup
                      trigger={this.renderDropZone()}
                      content={this.renderPopupContent()}
                      open={popupOpen}
                      on='click'
                      position={'top center'}
                      hideOnScroll
                  />
                </Grid.Column>
            )}
            {(!treatmentPrescription && readOnly) && (
                <Grid.Column width={6}>
                  <DropZone
                      message={getMessage("treatmentPrescription.noTreatmentPrescription")}
                      disabled={true}
                  />
                </Grid.Column>
            )}
          </Grid.Row>
        </PxGrid>
    );
  };

  renderDropZone = () => {
    const {getMessage} = this.props;
    const {uploading} = this.state;

    return (
        <DropZone
            multiple={false}
            message={getMessage("treatmentPrescription.upload")}
            onDropAccepted={this.onDropAccepted}
            onDropRejected={this.onDropRejected}
            disabled={uploading}
        />
    );
  };

  renderTreatmentPrescriptionInformation = () => {
    const {treatmentPrescription, uploading, uploadingProgress} = this.state;
    const {getMessage, readOnly} = this.props;

    const fileName = treatmentPrescription ? treatmentPrescription.fileName : undefined;
    const fileType = treatmentPrescription ? treatmentPrescription.fileType : undefined;

    return (
        <Grid.Column width={6}>
          <TreatmentPrescriptionBox>
            <Grid>
              <Grid.Row>
                <Grid.Column width={12}>
                  <NoOverflowDiv>{fileName}</NoOverflowDiv>
                  <FileType>
                    {fileType}
                  </FileType>
                </Grid.Column>
                <Grid.Column width={4}>
                  <StyledButtonContainer>
                    <div>
                      <StyledButton icon
                                    type='button'
                                    disabled={uploading}
                                    onClick={() => this.setPreviewOpen(true)}
                      >
                        <Popup
                            trigger={<Icon style={{color: "black"}} name='file alternate outline'/>}
                            content={getMessage('treatmentPrescription.preview')}
                            size='small'
                            position='top center'
                            style={PopupStyle}
                            disabled={uploading}
                            inverted
                        />
                      </StyledButton>
                    </div>
                    <DeleteActionButton
                        hoverMessage={getMessage('treatmentPrescription.delete')}
                        popupMessage={getMessage("treatmentPrescription.confirmDelete")}
                        onConfirm={this.deleteTreatmentPrescription}
                        trigger={<Icon style={{color: "black"}} name='trash alternate outline'/>}
                        disabled={uploading || readOnly}
                    />
                  </StyledButtonContainer>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            {uploading ? <Progress percent={uploadingProgress} attached='bottom' success/> : ""}
          </TreatmentPrescriptionBox>
        </Grid.Column>
    );
  };

  onDropAccepted = (acceptedFiles: File[]) => {
    const {treatmentPrescription} = this.state;
    const file: File = acceptedFiles[0];

    if (treatmentPrescription) {
      this.setState({popupOpen: true, file});
    } else {
      this.uploadFile(file);
    }
  };

  onDropRejected = (rejections: FileRejection[]) => {
    const errorCodes = rejections
        .flatMap(r => r.errors.map(e => e.code));
    const uniqueErrorCodes = Array.from(new Set(errorCodes));

    this.setState({errorCodes: uniqueErrorCodes});
  };

  toggleAccordion = (active: boolean) => {
    this.setState({active})
  };

  deleteTreatmentPrescription = () => {
    const {setTempTreatmentId} = this.props;
    const {cancelTokenSource, treatmentId, tempTreatmentId} = this.state;

    if (treatmentId) {
      deleteTreatmentPrescription(treatmentId, cancelTokenSource)
          .then(() => {
            this.setState({treatmentPrescription: undefined})
          });
    } else {
      deleteTempTreatmentPrescription(tempTreatmentId!, cancelTokenSource)
          .then(() => {
            setTempTreatmentId(undefined);
            this.setState({
              treatmentPrescription: undefined,
              tempTreatmentId: undefined
            });
          })
    }

  };

  setErrorCodes = (errorCodes?: Array<string>) => {
    this.setState({errorCodes});
  };

  onUploadProgress = (progressEvent: ProgressEvent) => {
    let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

    this.setState({uploadingProgress: percentCompleted});
  };

  togglePopup = () => {
    this.setState(({popupOpen}: State) => ({
      popupOpen: !popupOpen
    }));
  };

  renderPopupContent = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column>
              <Header size="small">{getMessage("treatmentPrescription.confirmUpload")}</Header>
            </Grid.Column>
          </Grid.Row>
          <FormButtonsRow>
            <Button compact primary onClick={this.popupAccept}>{getMessage("button.yes")}</Button>
            <Button compact onClick={this.togglePopup}>{getMessage("button.no")}</Button>
          </FormButtonsRow>
        </PxGrid>
    );
  };

  popupAccept = () => {
    const {file} = this.state;

    this.togglePopup();
    this.uploadFile(file!);
  };

  uploadFile = (file: File) => {
    const {cancelTokenSource, treatmentId, tempTreatmentId, errorCodes} = this.state;
    const {setUploading, setTempTreatmentId} = this.props;

    const treatmentPrescription: TreatmentPrescriptionDto = {
      fileName: file.name,
      fileType: file.type
    };

    this.setState({
      uploading: true,
      uploadingProgress: 0,
      treatmentPrescription
    });

    setUploading(true);

    if (treatmentId) {
      addTreatmentPrescription(treatmentId, file, this.onUploadProgress, cancelTokenSource)
          .then(() => {
          })
          .catch(() => {
            let newErrorCodes = errorCodes ? [...errorCodes, "general"] : ["general"];
            this.setState({
              errorCodes: newErrorCodes,
              treatmentPrescription: undefined
            });
          })
          .finally(() => {
            this.setState({
              uploading: false,
              uploadingProgress: undefined,
              file: undefined
            });
            setUploading(false);
          });
    } else {
      addTempTreatmentPrescription(tempTreatmentId, file, this.onUploadProgress, cancelTokenSource)
          .then((tempTreatmentId: number) => {
            setTempTreatmentId(tempTreatmentId);
            this.setState({tempTreatmentId});
          })
          .catch(() => {
            let newErrorCodes = errorCodes ? [...errorCodes, "general"] : ["general"];
            this.setState({
              errorCodes: newErrorCodes,
              treatmentPrescription: undefined
            });
          })
          .finally(() => {
            this.setState({
              uploading: false,
              uploadingProgress: undefined,
              file: undefined
            });
            setUploading(false);
          });
    }

  };

  setPreviewOpen = (previewOpen: boolean) => {
    const {cancelTokenSource, treatmentId, tempTreatmentId} = this.state;

    const id = treatmentId ? treatmentId : tempTreatmentId!;

    if (previewOpen) {
      getTreatmentDownloadKey(id, cancelTokenSource)
          .then(treatmentDownloadKey => {
            this.setState({
              treatmentDownloadKey,
              previewOpen
            });
          });
    } else {
      this.setState({
        treatmentDownloadKey: undefined,
        previewOpen
      });
    }
  };

}

export default withMessages(TreatmentPrescriptionSegment);
